import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, Checkbox, Divider, FormControlLabel, Typography } from '@mui/material';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { addDays, today } from '../labels';
import { Loader, NeedsHousehold, PageHeader, useConfirm, useHousehold } from '../common';
import { useTaskActions } from './taskActions';
import { useGoBack } from '../editing';

/** The occurrence on its day (or overdue); the page may be opened from a link, without it in memory. */
async function findOccurrence(id: T.Guid, date: T.DateOnly | null): Promise<T.TaskOccurrence> {
  const from = date ?? today();
  const agenda = await api.getTaskAgenda(from, date ?? addDays(from, 6));
  const found = agenda.occurrences.concat(agenda.overdue).find(o => o.publicId === id);
  if (!found) throw new Error('Esta tarefa já não está na agenda.');
  return found;
}

/** The steps of one occurrence: each tick is saved at once; it can be completed with steps left (after a warning). */
const TaskChecklistPage: React.FC = () => {
  const { occurrenceId } = useParams();
  const [search] = useSearchParams();
  const location = useLocation();
  const { members } = useHousehold();
  const goBack = useGoBack('/casa/tarefas');
  const [confirm, confirmDialog] = useConfirm();
  const passed = (location.state as { occurrence?: T.TaskOccurrence } | null)?.occurrence;
  const [occurrence, setOccurrence] = useState<T.TaskOccurrence | null>(passed && passed.publicId === occurrenceId ? passed : null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const date = search.get('data');

  const load = async () => {
    if (!occurrenceId) return;
    try {
      setLoadError(null);
      setOccurrence(await findOccurrence(occurrenceId, date));
    } catch (e: any) {
      setLoadError(e.message);
    }
  };
  const actions = useTaskActions(members, load);

  useEffect(() => {
    if (!occurrence) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occurrenceId]);

  const set = async (item: T.ChecklistItem, done: boolean) => {
    if (!occurrence || item.isDone === done) return;
    setError(null);
    setBusy(true);
    try {
      setOccurrence(await api.setChecklistItem(occurrence.publicId, item.text, done));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!occurrence) return;
    const left = occurrence.checklist.filter(i => !i.isDone).length;
    if (left > 0 && !await confirm({
      title: 'Concluir',
      text: left === 1 ? 'Falta 1 passo. Concluir mesmo assim?' : `Faltam ${left} passos. Concluir mesmo assim?`,
      confirmText: 'Concluir',
    })) return;
    if (await actions.complete(occurrence)) goBack();
  };

  const doneCount = occurrence?.checklist.filter(i => i.isDone).length ?? 0;

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Loader loading={!occurrence && !loadError} error={loadError} onRetry={load} hasData={occurrence !== null}>
        {occurrence && (
          <>
            <PageHeader title={occurrence.title} subtitle={`${doneCount} de ${occurrence.checklist.length} passos feitos`} />
            {occurrence.notes && <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-line' }}>📝 {occurrence.notes}</Typography>}
            <Card variant="outlined">
              {occurrence.checklist.map((item, i) => (
                <React.Fragment key={item.text}>
                  {i > 0 && <Divider />}
                  <FormControlLabel
                    sx={{ m: 0, px: 1, py: 0.5, width: '100%' }}
                    control={<Checkbox checked={item.isDone} disabled={busy} onChange={e => set(item, e.target.checked)} />}
                    label={
                      <Typography sx={{ textDecoration: item.isDone ? 'line-through' : undefined, color: item.isDone ? 'text.secondary' : undefined }}>
                        {item.text}
                      </Typography>
                    }
                  />
                </React.Fragment>
              ))}
            </Card>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              {occurrence.status === 'Pending' && <Button variant="contained" onClick={complete} disabled={busy}>Concluir tarefa</Button>}
              <Button onClick={goBack}>Voltar</Button>
            </Box>
          </>
        )}
      </Loader>
      {actions.element}
      {confirmDialog}
    </Box>
  );
};

const TaskChecklistRoute: React.FC = () => <NeedsHousehold><TaskChecklistPage /></NeedsHousehold>;
export default TaskChecklistRoute;
